/**
 * Guided tutorial: Create your first character.
 * Progress is derived from the live workflow graph and run state — not mere Next clicks.
 * Unavailable / mock-worker capabilities are labeled; the tutorial never claims them as complete production results.
 */
import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { CHARACTER_NODE_CATALOG } from '../features/characterNodeCatalog';

const STORAGE_KEY = 'bakeonator.character.tutorial.v1';

function loadProgress(recordId) {
  try {
    const raw = JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');
    return raw[recordId] || { dismissedHints: [] };
  } catch {
    return { dismissedHints: [] };
  }
}

function saveProgress(recordId, progress) {
  try {
    const raw = JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');
    raw[recordId] = progress;
    localStorage.setItem(STORAGE_KEY, JSON.stringify(raw));
  } catch {
    /* ignore */
  }
}

function hasNode(graph, type) {
  return (graph?.nodes || []).some((n) => n.type === type);
}

function hasEdge(graph, sourceType, targetType) {
  const nodes = Object.fromEntries((graph?.nodes || []).map((n) => [n.id, n]));
  return (graph?.edges || []).some((e) => {
    const s = nodes[e.source];
    const t = nodes[e.target];
    return s?.type === sourceType && t?.type === targetType;
  });
}

function imageWithPrompt(graph) {
  return (graph?.nodes || []).some(
    (n) => n.type === 'image' && (n.file_id || n.image) && String(n.prompt || n.text || '').trim()
  );
}

function assessSteps({ graph, runsByNode }) {
  const conceptNode = (graph?.nodes || []).find((n) => n.type === 'create_images');
  const conceptRun = conceptNode ? runsByNode?.[conceptNode.id] : null;
  const conceptSucceeded = Boolean(conceptRun?.latest_success);
  const conceptApproved = Boolean(conceptNode?.approved);
  const meshNode = (graph?.nodes || []).find((n) => n.type === 'create_3d');
  const meshSucceeded = Boolean(meshNode && runsByNode?.[meshNode.id]?.latest_success);
  const meshApproved = Boolean(meshNode?.approved);
  const hasOrtho = hasNode(graph, 'create_ortho');
  const hasTexture = hasNode(graph, 'texture_pbr');
  const hasRig = hasNode(graph, 'rig_skin');
  const hasExport = hasNode(graph, 'export_character');
  const exportNode = (graph?.nodes || []).find((n) => n.type === 'export_character');
  const exportDone = Boolean(exportNode?.last_export?.package_id);

  return [
    {
      id: 'create',
      title: 'Create a character workflow',
      done: true,
      detail: 'You are on a character production record.',
    },
    {
      id: 'materials',
      title: 'Add a brief and image references',
      done: hasNode(graph, 'text') && hasNode(graph, 'image') && imageWithPrompt(graph),
      detail:
        'Add a Character brief and Image nodes. Use Replace under an image to upload or pick an existing project file — identity, ports, and instructions stay. If downstream work is approved, reopen those approvals first.',
    },
    {
      id: 'connect-concepts',
      title: 'Add and connect Generate Concepts',
      done:
        hasNode(graph, 'create_images') &&
        (hasEdge(graph, 'text', 'create_images') ||
          hasEdge(graph, 'combine', 'create_images') ||
          hasEdge(graph, 'image', 'create_images')),
      detail:
        'Right-click the canvas → Generate Concepts. Connect Brief (text) and/or References (image) ports. Required vs optional ports are labeled on the node.',
    },
    {
      id: 'run-concepts',
      title: 'Run concepts and review alternatives',
      done: conceptSucceeded,
      blocked: CHARACTER_NODE_CATALOG.create_images.availability === 'worker_mock',
      blockReason:
        'create_images currently returns labeled worker mock payloads — not production concept art. You may still run it for rehearsal; mark results carefully.',
      detail:
        'Save, then use Run on the node (or Run all in the toolbar). The info icon shows predicted usage — Estimate unavailable until a real rate card exists; hovering never starts a job. Actual usage appears separately after completion.',
    },
    {
      id: 'approve-concept',
      title: 'Approve a chosen concept result',
      done: conceptApproved,
      detail:
        'Approve pins a specific run version. Changing upstream inputs after approval requires explicit reopen and invalidates dependent approvals.',
    },
    {
      id: 'ortho',
      title: 'Generate orthographic views',
      done: false,
      blocked: true,
      blockReason: CHARACTER_NODE_CATALOG.create_ortho.availabilityDetail,
      detail: 'Add Orthographic views when available; connect an approved concept image.',
    },
    {
      id: 'mesh',
      title: 'Generate, inspect and approve the mesh',
      done: meshSucceeded && meshApproved,
      blocked: CHARACTER_NODE_CATALOG.create_3d.availability === 'worker_mock',
      blockReason: CHARACTER_NODE_CATALOG.create_3d.availabilityDetail,
      detail: 'Add Generate mesh, connect views/concept materials, Run, then Approve. Mock GLB cards are not USD export.',
    },
    {
      id: 'texture-rig',
      title: 'Complete texture and rigging operations',
      done: false,
      blocked: true,
      blockReason: [
        CHARACTER_NODE_CATALOG.texture_pbr.availabilityDetail,
        CHARACTER_NODE_CATALOG.rig_skin.availabilityDetail,
      ].join(' '),
      detail: hasTexture || hasRig || hasOrtho
        ? 'Catalog nodes may be placed for planning but cannot Run until capabilities exist.'
        : 'Texture/PBR and Rig/skin catalog entries are unavailable for execution.',
    },
    {
      id: 'history',
      title: 'Use branching snapshot history',
      done: false,
      detail:
        'Open Studio → Branching Snapshot History. Process draft freezes the graph; restore a checkpoint or start an outfit alternative branch without losing the original. Connections describe material dependencies; history branches describe alternative revisions.',
      hintOnly: true,
    },
    {
      id: 'export',
      title: 'Export and open the completed character',
      done: exportDone,
      blocked: false,
      detail: hasExport
        ? exportDone
          ? 'bakeonator_package_v1 persisted on the Export node (approved run refs + files). OpenUSD Character Stage viewing is a separate demo path, not this package.'
          : 'Connect approved materials, then Build export package. Supported format today: bakeonator_package_v1 (not USD/GLB).'
        : 'Add Export character when packaging approved artifacts.',
    },
  ];
}

export default function FirstCharacterTutorial({
  projectId,
  recordId,
  department,
  graph,
  runsByNode,
  onClose,
  onFocusNode,
}) {
  const [progress, setProgress] = useState(() => loadProgress(recordId));
  const steps = useMemo(
    () => assessSteps({ graph, runsByNode }),
    [graph, runsByNode]
  );
  const completed = steps.filter((s) => s.done && !s.blocked).length;
  const blockedCount = steps.filter((s) => s.blocked && !s.done).length;

  const dismiss = () => {
    const next = { ...progress, dismissedAt: new Date().toISOString() };
    setProgress(next);
    saveProgress(recordId, next);
    onClose?.();
  };

  return (
    <div className="workflow-tutorial-backdrop" role="presentation" onClick={dismiss}>
      <aside
        className="workflow-tutorial"
        role="dialog"
        aria-modal="true"
        aria-labelledby="first-character-tutorial-title"
        onClick={(e) => e.stopPropagation()}
      >
        <header>
          <p className="production-eyebrow">GUIDED · TIGER ADVENTURER EXAMPLE</p>
          <h2 id="first-character-tutorial-title">Create your first character</h2>
          <p>
            Progress tracks your saved workflow graph and real run state. You can supply your own brief
            and references. Switch freely between this canvas and Studio Guided view — they share the
            same production record.
          </p>
          <p role="status">
            {completed} checklist items satisfied · {blockedCount} blocked by missing integrations
          </p>
        </header>
        <ol className="workflow-tutorial-steps">
          {steps.map((step) => (
            <li key={step.id} data-state={step.done ? 'done' : step.blocked ? 'blocked' : 'open'}>
              <div>
                <strong>{step.title}</strong>
                <p>{step.detail}</p>
                {step.blocked ? (
                  <p className="workflow-capability-badge unavailable" role="status">
                    Blocked: {step.blockReason}
                  </p>
                ) : null}
                {step.done && !step.blocked ? (
                  <p className="workflow-capability-badge approved" role="status">Satisfied by current graph/runs</p>
                ) : null}
              </div>
            </li>
          ))}
        </ol>
        <p className="production-footnote">
          Workflow connections describe material dependencies. History branches describe alternative
          revisions of the whole graph. Approved results cannot be silently overwritten — reopen first.
          Run all follows a server plan (skip approved/unchanged, stop at approval gates). Predicted
          costs and actual recorded usage are labeled separately; Estimate unavailable means no rate
          card is registered yet — never invented credits.
        </p>
        <div className="d-flex flex-wrap gap-2">
          <Link
            className="btn btn-outline-secondary"
            to={`/console/projects/${projectId}/departments/${department}/${recordId}/studio`}
            onClick={() => {
              saveProgress(recordId, progress);
            }}
          >
            Open Studio (Canvas / Guided / History)
          </Link>
          <button type="button" className="btn btn-primary" onClick={dismiss}>
            Continue on canvas
          </button>
        </div>
      </aside>
    </div>
  );
}
